import { RuntimeConfiguration } from './runtime-configuration';

/**
 * The roots `BaseConfiguration` documents as **not** organization-scoped: the object endpoints carry no
 * `/organizations/<orgKey>` segment, and the two administration surfaces put the tenant inside their own
 * paths, so scoping these would produce a doubled segment.
 */
const UNSCOPED_ROOTS: ReadonlySet<string> = new Set(['OBJECT_STORE_SERVICE_ROOT', 'ORG_ADMIN_SERVICE_ROOT', 'PLATFORM_ADMIN_SERVICE_ROOT']);

/**
 * Appends `/organizations/<orgKey>` to every organization-scoped service root.
 *
 * `BACKEND_SERVICE_ROOT` and the per-feature roots are meant to *be* `<host>/organizations/<orgKey>` —
 * the tenant is part of the root so that no screen threads it through. Every other shell knows its
 * organization when it is deployed and writes it into `run-time-conf`. This one serves every customer
 * from one image and learns the organization from the URL, so the files can only name the host, and the
 * organization has to be added here, before bootstrap. Left as the bare host, every designer resource
 * resolves to `/api/<resource>`, which the custom backend does not map: it answers 404 "No static
 * resource" to the header's app-name lookup, every designer list, the starter install and the
 * translations fallback alike.
 *
 * Every `*_SERVICE_ROOT` key is scoped except {@link UNSCOPED_ROOTS}, rather than a fixed list of
 * feature keys: a feature root this file has never heard of is org-scoped by the configuration contract,
 * and one that is absent falls back to the scoped `BACKEND_SERVICE_ROOT` anyway.
 *
 * With no organization the configuration is handed back untouched: there is no tenant to scope to, and
 * the only routes left are the ones that need no backend.
 */
export function withOrganizationRoots(runtimeConfiguration: RuntimeConfiguration, orgKey?: string): RuntimeConfiguration {
  if (!orgKey) return runtimeConfiguration;

  const scoped = Object.fromEntries(
    Object.entries(runtimeConfiguration.BASE_CONFIGURATION).map(([key, value]) =>
      key.endsWith('_SERVICE_ROOT') && !UNSCOPED_ROOTS.has(key) && typeof value === 'string'
        ? [key, `${withoutTrailingSlashes(value)}/organizations/${encodeURIComponent(orgKey)}`]
        : [key, value],
    ),
  );
  return { ...runtimeConfiguration, BASE_CONFIGURATION: { ...runtimeConfiguration.BASE_CONFIGURATION, ...scoped } };
}

/** A loop rather than `/\/+$/`, whose backtracking is super-linear on a long run of slashes. */
function withoutTrailingSlashes(root: string): string {
  let end = root.length;
  while (end > 0 && root[end - 1] === '/') end--;
  return root.slice(0, end);
}
